import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { inr } from "../lib/format";
import { TID } from "../lib/testIds";

export const PackageCard = ({ pkg, discount = 0, months = 0, index = 0 }) => {
  const effective = Math.max(0, pkg.dev_fee - (discount || 0));
  const rec = !!pkg.recommended;
  return (
    <motion.article
      data-testid={TID.pricing.card(pkg.id)}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className={`relative flex h-full flex-col border p-6 sm:p-8 ${rec ? "card-glow border-ink/15 bg-surface" : "border-forest/10 bg-surface"}`}
    >
      {rec && (
        <span
          data-testid={TID.pricing.badge(pkg.id)}
          className="absolute -top-3 left-1/2 -translate-x-1/2 border border-crimson/60 bg-paper px-4 py-1 font-mono text-[11px] font-bold tracking-[0.3em] text-crimson"
        >
          RECOMMENDED
        </span>
      )}

      <div className="font-display text-sm font-extrabold uppercase tracking-[0.25em] text-crimson">PACKAGE {pkg.number}</div>
      <h3 className={`mt-3 font-display text-3xl font-bold ${rec ? "text-crimson" : "text-ink"}`}>{pkg.name}</h3>
      <p className="mt-3 text-sm leading-relaxed text-ink/70">{pkg.description}</p>

      <div className="mt-8 space-y-4">
        <div data-testid={TID.pricing.fee(pkg.id)}>
          <div className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-ink/70">Development — one-time</div>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            {discount > 0 && <span className="text-lg text-ink/55 line-through">{inr(pkg.dev_fee)}</span>}
            {pkg.price_prefix && <span className="font-mono text-xs uppercase tracking-wider text-crimson/90">{pkg.price_prefix}</span>}
            <span data-testid={discount > 0 ? TID.pricing.effFee(pkg.id) : undefined} className={`font-display text-4xl font-extrabold ${rec ? "text-crimson" : "text-ink"}`}>
              {inr(discount > 0 ? effective : pkg.dev_fee)}
            </span>
          </div>
          {discount > 0 && (
            <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-600">
              With {months}-month commitment — saves {inr(discount)}
            </div>
          )}
        </div>
        <div className="h-px bg-crimson/10" />
        <div>
          <div className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-ink/70">Maintenance — monthly</div>
          <div className="mt-1 font-display text-2xl font-bold text-ink">
            {inr(pkg.maintenance)}
            <span className="font-body text-sm font-medium text-ink/65"> /month</span>
          </div>
        </div>
      </div>

      <ul className="mt-8 flex-1 space-y-3">
        {pkg.features.filter(Boolean).map((f, i) => (
          <li key={i} className="flex gap-3 text-sm text-ink/75">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-forest" strokeWidth={2.5} />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {pkg.scope_note ? <p className="mt-4 border-l border-ink/15 pl-3 text-xs leading-relaxed text-ink/65">{pkg.scope_note}</p> : null}

      {pkg.ideal_for ? <div className="mt-6 border-t border-ink/10 pt-4">
          <span className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-crimson">Ideal for</span>
          <p className="mt-1.5 text-sm font-medium leading-relaxed text-ink/80">{pkg.ideal_for}</p>
        </div> : null}

      <Link
        to={`/contact?package=${pkg.id}`}
        data-testid={TID.pricing.cta(pkg.id)}
        className={`group mt-8 inline-flex items-center justify-between px-6 py-4 font-display text-sm font-bold transition-colors ${
          rec ? "bg-crimson text-white hover:bg-crimsondark" : "border border-ink/10 text-ink hover:border-forest/60 hover:text-crimson"
        }`}
      >
        {pkg.cta}
        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </Link>
    </motion.article>
  );
};
